import type { Divergence, PriceTransfer, PriceVatRuleItem, SourceValue } from './types'

/**
 * Цена при переносе между источниками (требования 17, решение 9.6). Значение цены приходит числом строкой в инвариантной культуре;
 * счёт — в целых копейках, округление до копейки «половина вверх», как у службы. С «+НДС» ставка — товара в источнике с НДС (в строке
 * цены: при «добавить НДС» — у цели, при «вычесть НДС» — у образца); ставки нет — служба откажет в переносе, и окно говорит об этом заранее.
 */
export interface PriceOutcome {
  /** Цена сейчас в целевом источнике, копейки; `null` — цены там нет. */
  before: number | null
  /** Цена, которая будет записана, копейки; `null` — перенести нельзя (`reason`). */
  after: number | null
  reason: string | null
}

function kopecksOf(value: SourceValue | undefined): number | null {
  if (value === undefined || !value.present || value.value === null) return null
  const rubles = Number(value.value)
  return Number.isFinite(rubles) ? Math.round(rubles * 100) : null
}

export function priceOutcome(from: SourceValue | undefined, target: SourceValue | undefined, transfer: PriceTransfer): PriceOutcome {
  const before = kopecksOf(target)
  const source = kopecksOf(from)
  if (source === null) return { before, after: null, reason: 'В источнике, откуда берётся цена, её нет.' }
  if (transfer === 'same') return { before, after: source, reason: null }
  const percent = transfer === 'add-vat' ? target?.vatPercent : from?.vatPercent
  if (percent === null || percent === undefined)
    return {
      before,
      after: null,
      reason:
        transfer === 'add-vat'
          ? 'У товара в целевом источнике нет ставки НДС (или он без НДС): добавить НДС не по чему.'
          : 'У товара в источнике-образце нет ставки НДС (или он без НДС): вычесть НДС не по чему.',
    }
  const after = transfer === 'add-vat' ? Math.round((source * (100 + percent)) / 100) : Math.round((source * 100) / (100 + percent))
  return { before, after, reason: null }
}

/**
 * Правила цены для связей товаров (`PUT price-vat-rules`): «+НДС» означает, что второй источник пары — с НДС. Поэтому «добавить НДС» —
 * пара образец → цель, «вычесть НДС» — пара цель → образец. Строки без общего товара правила не получают: связи ещё нет.
 */
export function priceRulesFor(rows: readonly Divergence[], from: string, targets: readonly string[], transfer: PriceTransfer): PriceVatRuleItem[] {
  return rows.flatMap((row) => {
    const productId = row.productId
    if (row.dataKind !== 'price' || productId === null) return []
    return targets
      .filter((target) => target !== from)
      .map((target) =>
        transfer === 'remove-vat'
          ? { productId, firstResourceId: target, secondResourceId: from, rule: 'plus-vat' as const }
          : { productId, firstResourceId: from, secondResourceId: target, rule: transfer === 'add-vat' ? ('plus-vat' as const) : ('same' as const) },
      )
  })
}
